import { User, RoleDefinition, RolePermission } from '../types';

export const mockUsers: User[] = [
  {
    id: '1',
    username: 'admin',
    email: 'admin@example.com',
    fullName: 'Admin User',
    role: 'admin',
    avatar: '/avatars/admin.jpg',
    createdAt: '2025-01-01T00:00:00Z',
    lastLogin: '2025-05-05T08:30:00Z',
    isActive: true,
    twoFactorEnabled: true,
  },
  {
    id: '2',
    username: 'editor1',
    email: 'editor@example.com',
    fullName: 'Editor User',
    role: 'editor',
    avatar: '/avatars/editor.jpg',
    createdAt: '2025-01-15T00:00:00Z',
    lastLogin: '2025-05-04T14:22:00Z',
    isActive: true,
    twoFactorEnabled: false,
  },
  {
    id: '3',
    username: 'contributor1',
    email: 'contributor@example.com',
    fullName: 'Contributor User',
    role: 'contributor',
    avatar: '/avatars/contributor.jpg',
    createdAt: '2025-02-01T00:00:00Z',
    lastLogin: '2025-05-03T09:15:00Z',
    isActive: true,
    twoFactorEnabled: false,
  },
  {
    id: '4',
    username: 'subscriber1',
    email: 'subscriber@example.com',
    fullName: 'Subscriber User',
    role: 'subscriber',
    avatar: '/avatars/subscriber.jpg',
    createdAt: '2025-02-15T00:00:00Z',
    lastLogin: '2025-04-28T16:45:00Z',
    isActive: true,
    twoFactorEnabled: false,
  },
  {
    id: '5',
    username: 'editor2',
    email: 'editor2@example.com',
    fullName: 'Second Editor',
    role: 'editor',
    avatar: '/avatars/editor2.jpg',
    createdAt: '2025-03-01T00:00:00Z',
    lastLogin: '2025-05-02T11:30:00Z',
    isActive: true,
    twoFactorEnabled: true,
  },
  {
    id: '6',
    username: 'inactive',
    email: 'inactive@example.com',
    fullName: 'Inactive User',
    role: 'contributor',
    avatar: '/avatars/inactive.jpg',
    createdAt: '2025-03-15T00:00:00Z',
    lastLogin: '2025-04-01T10:00:00Z',
    isActive: false,
    twoFactorEnabled: false,
  },
];

// Define common permissions
const createDocumentPermissions = (role: string): RolePermission[] => [
  {
    id: 'documents.view',
    name: 'View Documents',
    description: 'Can view documents in the system',
    category: 'documents',
    isGranted: true,
  },
  {
    id: 'documents.create',
    name: 'Create Documents',
    description: 'Can create new documents',
    category: 'documents',
    isGranted: role !== 'subscriber',
  },
  {
    id: 'documents.edit',
    name: 'Edit Documents',
    description: 'Can edit existing documents',
    category: 'documents',
    isGranted: role !== 'subscriber',
  },
  {
    id: 'documents.delete',
    name: 'Delete Documents',
    description: 'Can delete documents',
    category: 'documents',
    isGranted: role === 'admin' || role === 'editor',
  },
  {
    id: 'documents.publish',
    name: 'Publish Documents',
    description: 'Can publish documents',
    category: 'documents',
    isGranted: role === 'admin' || role === 'editor',
  },
];

const createCollectionPermissions = (role: string): RolePermission[] => [
  {
    id: 'collections.view',
    name: 'View Collections',
    description: 'Can view collections in the system',
    category: 'collections',
    isGranted: true,
  },
  {
    id: 'collections.create',
    name: 'Create Collections',
    description: 'Can create new collections',
    category: 'collections',
    isGranted: role === 'admin' || role === 'editor',
  },
  {
    id: 'collections.edit',
    name: 'Edit Collections',
    description: 'Can edit existing collections',
    category: 'collections',
    isGranted: role === 'admin' || role === 'editor',
  },
  {
    id: 'collections.delete',
    name: 'Delete Collections',
    description: 'Can delete collections',
    category: 'collections',
    isGranted: role === 'admin',
  },
];

const createProfilePermissions = (role: string): RolePermission[] => [
  {
    id: 'profiles.view',
    name: 'View Profiles',
    description: 'Can view profiles in the system',
    category: 'profiles',
    isGranted: true,
  },
  {
    id: 'profiles.create',
    name: 'Create Profiles',
    description: 'Can create new profiles',
    category: 'profiles',
    isGranted: role === 'admin' || role === 'editor',
  },
  {
    id: 'profiles.edit',
    name: 'Edit Profiles',
    description: 'Can edit existing profiles',
    category: 'profiles',
    isGranted: role === 'admin' || role === 'editor',
  },
  {
    id: 'profiles.delete',
    name: 'Delete Profiles',
    description: 'Can delete profiles',
    category: 'profiles',
    isGranted: role === 'admin',
  },
];

const createUserPermissions = (role: string): RolePermission[] => [
  {
    id: 'users.view',
    name: 'View Users',
    description: 'Can view users in the system',
    category: 'users',
    isGranted: role === 'admin' || role === 'editor',
  },
  {
    id: 'users.create',
    name: 'Create Users',
    description: 'Can create new users',
    category: 'users',
    isGranted: role === 'admin',
  },
  {
    id: 'users.edit',
    name: 'Edit Users',
    description: 'Can edit existing users',
    category: 'users',
    isGranted: role === 'admin',
  },
  {
    id: 'users.delete',
    name: 'Delete Users',
    description: 'Can delete users',
    category: 'users',
    isGranted: role === 'admin',
  },
];

const createKioskPermissions = (role: string): RolePermission[] => [
  {
    id: 'kiosk.view',
    name: 'View Kiosk',
    description: 'Can view kiosk settings',
    category: 'kiosk',
    isGranted: role === 'admin' || role === 'editor',
  },
  {
    id: 'kiosk.configure',
    name: 'Configure Kiosk',
    description: 'Can configure kiosk settings',
    category: 'kiosk',
    isGranted: role === 'admin',
  },
];

const createSettingsPermissions = (role: string): RolePermission[] => [
  {
    id: 'settings.view',
    name: 'View Settings',
    description: 'Can view system settings',
    category: 'settings',
    isGranted: role === 'admin',
  },
  {
    id: 'settings.edit',
    name: 'Edit Settings',
    description: 'Can edit system settings',
    category: 'settings',
    isGranted: role === 'admin',
  },
  {
    id: 'settings.api',
    name: 'Manage API',
    description: 'Can manage API settings and tokens',
    category: 'settings',
    isGranted: role === 'admin' || role === 'subscriber',
  },
];

const createAnalyticsPermissions = (role: string): RolePermission[] => [
  {
    id: 'analytics.view',
    name: 'View Analytics',
    description: 'Can view analytics data',
    category: 'analytics',
    isGranted: role === 'admin',
  },
  {
    id: 'analytics.export',
    name: 'Export Analytics',
    description: 'Can export analytics data',
    category: 'analytics',
    isGranted: role === 'admin',
  },
];

export const mockRoles: RoleDefinition[] = [
  {
    id: 'admin',
    name: 'Administrator',
    description: 'Full access to all system features and settings',
    permissions: [
      ...createDocumentPermissions('admin'),
      ...createCollectionPermissions('admin'),
      ...createProfilePermissions('admin'),
      ...createUserPermissions('admin'),
      ...createKioskPermissions('admin'),
      ...createSettingsPermissions('admin'),
      ...createAnalyticsPermissions('admin'),
    ],
    isSystemRole: true,
  },
  {
    id: 'editor',
    name: 'Editor',
    description: 'Can manage content and approve contributor submissions',
    permissions: [
      ...createDocumentPermissions('editor'),
      ...createCollectionPermissions('editor'),
      ...createProfilePermissions('editor'),
      ...createUserPermissions('editor'),
      ...createKioskPermissions('editor'),
      ...createSettingsPermissions('editor'),
      ...createAnalyticsPermissions('editor'),
    ],
    isSystemRole: true,
  },
  {
    id: 'contributor',
    name: 'Contributor',
    description: 'Can submit content for approval by editors',
    permissions: [
      ...createDocumentPermissions('contributor'),
      ...createCollectionPermissions('contributor'),
      ...createProfilePermissions('contributor'),
      ...createUserPermissions('contributor'),
      ...createKioskPermissions('contributor'),
      ...createSettingsPermissions('contributor'),
      ...createAnalyticsPermissions('contributor'),
    ],
    isSystemRole: true,
  },
  {
    id: 'subscriber',
    name: 'Subscriber',
    description: 'Read-only access to content with API capabilities',
    permissions: [
      ...createDocumentPermissions('subscriber'),
      ...createCollectionPermissions('subscriber'),
      ...createProfilePermissions('subscriber'),
      ...createUserPermissions('subscriber'),
      ...createKioskPermissions('subscriber'),
      ...createSettingsPermissions('subscriber'),
      ...createAnalyticsPermissions('subscriber'),
    ],
    isSystemRole: true,
  },
];
